import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Divider, 
  FormControl, 
  FormHelperText, 
  TextField, 
  Select, 
  MenuItem, 
  InputLabel,
  Box,
  Chip
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { CoreForumEditor } from "./CoreForumEditor";
import { createNodeWithPartnerResponse } from "../../../utils/nodeCreationHelper";
import { parseContentToHtml } from '../../../utils/markdownParser';
import { GroupContextTypes } from '../../../types/groupContext.types.js';
import '../../../styles/NodeContentStyles.css'; // 導入節點內容樣式

/**
 * @typedef {import('../../../types/groupContext.types.js').GroupContext} GroupContext
 */

// 默認標籤配置
const DEFAULT_TAG_CONFIG = {
  idea: { label: '想法', emoji: '💡', color: '#F3E8FF' }
};

/**
 * CoreForumNodeCreator - 核心 Forum 节点创建组件
 * 
 * ⚠️ Stateless 原則：
 * - activityId, groupId, nodeId 應該由父組件明確提供（推薦）
 * - 若未提供，將從 sessionStorage 獲取（向後兼容，但會記錄警告）
 * - 若兩者都無效，將在提交時阻止操作並顯示錯誤
 * 
 * @param {Object} props
 * @param {Function} props.buildGroupContext - 子 mode 提供的 context 构建函数 (graphNodes, content, tag?) => GroupContext
 * @param {string} props.mode - 模式标识
 * @param {string} [props.activityId] - 活動 ID（推薦明確提供）
 * @param {string} [props.groupId] - 群組 ID（推薦明確提供）
 * @param {string} [props.nodeId] - 節點 ID（推薦明確提供，用於 nodePlan 模式）
 */
export const CoreForumNodeCreator = ({ 
  open, 
  onClose, 
  ws, 
  graphNodes, 
  graphEdges, // 新增：接收 edges 数据
  allTagConfigs = {},
  defaultTag = 'idea',
  parentNodeId = null,
  parentNodeContent = null, // 新增：要回覆的節點內容
  dialogTitle = '新增想法',
  isReply = false,
  showTags = true, // 新增：控制是否顯示標籤
  nodePlan = null, // 新增：支援 nodePlan 模式（用於 Sticker）
  buildGroupContext, // 子 mode 提供的 context 构建函数
  mode = 'tagged', // 模式标识
  activityId: propActivityId, // 從 props 獲取的 activityId
  groupId: propGroupId, // 從 props 獲取的 groupId
  nodeId: propNodeId // 從 props 獲取的 nodeId
}) => {
  const userName = localStorage.getItem('userName') || '匿名用户';
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [selectedTag, setSelectedTag] = useState(defaultTag);
  const [customTagName, setCustomTagName] = useState('');
  const [isCustomTag, setIsCustomTag] = useState(false);
  
  // nodePlan 模式：使用 nodePlan 的配置覆蓋預設值
  const isNodePlanMode = !!nodePlan;
  const safeNodePlan = nodePlan || {
    title: dialogTitle,
    label: '想法標題',
    helperMsg: '請為你的想法下一個清楚的標題，讓其他同學能快速了解你的重點',
    tags: defaultTag,
    note: null,
    newEdge: false
  };
  
  // 使用從 TagBasedForum 傳遞過來的標籤配置，如果沒有則使用默認配置
  const allTags = Object.keys(allTagConfigs).length > 0 ? allTagConfigs : DEFAULT_TAG_CONFIG;

  // Helper 函數：安全獲取標籤顯示文本
  const getTagDisplayText = (tagKey) => {
    const tag = allTags[tagKey];
    if (!tag) return 'idea';
    return `${tag.emoji || ''} ${tag.label || tagKey}`.trim();
  };

  // Helper 函數：安全獲取標籤顏色
  const getTagColor = (tagKey) => {
    const tag = allTags[tagKey];
    return tag?.color || '#E5E7EB';
  };
  
  const titleContentChange = (e) => {
    setTitle(e.target.value);
  };

  const handleTagChange = (e) => {
    const value = e.target.value;
    setSelectedTag(value);
    setIsCustomTag(value === 'custom');
    if (value !== 'custom') {
      setCustomTagName('');
    }
  };

  const handleCustomTagChange = (e) => {
    setCustomTagName(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isTitleValid = title.trim().length > 0;
    if (!isTitleValid || !content || content.length === 0) {
      return alert("請確定標題及內容都已輸入");
    }

    // 檢查自定義標籤
    if (isCustomTag && !customTagName.trim()) {
      return alert("請輸入自定義標籤名稱");
    }

    // ✅ Stateless 參數獲取：優先使用 props（顯式依賴），否則從 sessionStorage 獲取（隱式依賴）
    let activityId = propActivityId;
    let groupId = propGroupId;
    let nodeId = propNodeId;
    
    // 如果 props 未提供，嘗試從 sessionStorage 獲取（向後兼容）
    if (!activityId) {
      activityId = sessionStorage.getItem('activityId');
      console.warn('[CoreForumNodeCreator] activityId 未從 props 提供，從 sessionStorage 獲取。建議父組件明確傳遞此參數。');
    }
    
    if (!groupId) {
      groupId = sessionStorage.getItem('groupId');
      console.warn('[CoreForumNodeCreator] groupId 未從 props 提供，從 sessionStorage 獲取。建議父組件明確傳遞此參數。');
    }
    
    if (!nodeId && isNodePlanMode) {
      nodeId = sessionStorage.getItem('nodeId');
      console.warn('[CoreForumNodeCreator] nodeId 未從 props 提供，從 sessionStorage 獲取。建議父組件明確傳遞此參數。');
    }
    
    // ✅ 嚴格參數驗證：暴露問題而非靜默失敗
    if (!activityId || activityId === 'null') {
      console.error('[CoreForumNodeCreator] activityId 無效或未提供', {
        propActivityId,
        sessionStorageActivityId: sessionStorage.getItem('activityId'),
        mode
      });
      return alert('無法獲取活動 ID，請重新進入活動。\n（技術細節：activityId 參數未正確傳遞）');
    }
    
    if (!groupId || groupId === 'null') {
      console.error('[CoreForumNodeCreator] groupId 無效或未提供', {
        propGroupId,
        sessionStorageGroupId: sessionStorage.getItem('groupId'),
        mode
      });
      return alert('無法獲取群組 ID，請重新進入活動。\n（技術細節：groupId 參數未正確傳遞）');
    }

    // 決定最終使用的標籤
    const finalTag = isCustomTag ? customTagName.trim().toLowerCase() : selectedTag;

    // 決定最終標籤：nodePlan 模式使用 nodePlan.tags，一般模式使用 finalTag，如果都沒有則使用預設值
    const effectiveTag = isNodePlanMode 
      ? (safeNodePlan.tags || 'idea') 
      : (finalTag || 'idea');

    const ideaData = {
      title: title,
      content: content,
      tags: effectiveTag,
      author: userName,
      groupId: groupId
    };

    setLoading(true);
    
    // 使用共用的 createNodeWithPartnerResponse 模組
    await createNodeWithPartnerResponse({
      ideaData,
      activityId,
      groupId,
      ws,
      nodes: graphNodes,
      edges: graphEdges,
      buildGroupContext,
      mode,
      parentNodeId,
      isNodePlanMode,
      nodePlan: safeNodePlan,
      validateContext: true,
      onSuccess: () => {
        alert("新增成功");
        handleClose();
        setLoading(false);
      },
      onError: (error) => {
        alert("新增失敗");
        console.error('新增節點失敗:', error);
        setLoading(false);
      }
    });
  };

  const handleClose = () => {
    setTitle('');
    setContent('');
    setSelectedTag(defaultTag);
    setCustomTagName('');
    setIsCustomTag(false);
    onClose();
  };

  useEffect(() => {
    if (open) {
      const plan = nodePlan || {};
      const initialTitle = plan.initialTitle != null ? String(plan.initialTitle) : '';
      const initialContent = plan.initialContent != null ? String(plan.initialContent) : '';
      setTitle(initialTitle);
      setContent(initialContent);
      setSelectedTag(defaultTag);
      setCustomTagName('');
      setIsCustomTag(false);
    }
  }, [open, defaultTag, nodePlan]);
  
  // 當 defaultTag 改變時，更新選中的標籤（即使對話框是開啟狀態）
  useEffect(() => {
    setSelectedTag(defaultTag);
    setIsCustomTag(false);
    setCustomTagName('');
  }, [defaultTag]);
  
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isNodePlanMode ? "95vw" : "md"}
      fullWidth={!isNodePlanMode}
      scroll='body'
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <DialogTitle sx={{
        fontSize: '1.5rem',
        fontWeight: 600,
        color: '#1e293b',
        paddingBottom: '16px'
      }}>
        {isNodePlanMode ? safeNodePlan.title : dialogTitle}
      </DialogTitle>
      <Divider />
      
      <DialogContent>
        {/* nodePlan 模式：顯示 note */}
        {isNodePlanMode && safeNodePlan.note && (
          <Box sx={{ mb: 2 }}>
            <>
              正在回應：
              <div 
                className="node-content-container"
                dangerouslySetInnerHTML={{ __html: parseContentToHtml(safeNodePlan.note) }} 
                style={{padding: '10px'}} 
              />
            </>
          </Box>
        )}

        {/* 標籤選擇 - 只在非回覆模式且非 nodePlan 模式顯示 */}
        {!isReply && !isNodePlanMode && (
          <>
            <FormControl variant="outlined" fullWidth margin="normal">
              <InputLabel id="tag-select-label">選擇分類</InputLabel>
              <Select
                labelId="tag-select-label"
                id="tag-select"
                value={selectedTag}
                label="選擇分類"
                onChange={handleTagChange}
              >
                {Object.entries(allTags).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{config.emoji}</span>
                      <span>{config.label}</span>
                    </Box>
                  </MenuItem>
                ))}
                <MenuItem value="custom">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>✏️</span>
                    <span>自定義標籤</span>
                  </Box>
                </MenuItem>
              </Select>
              <FormHelperText>
                選擇適合的分類來幫助其他人更好地理解你的內容
              </FormHelperText>
            </FormControl>

            {/* 自定義標籤輸入框 */}
            {isCustomTag && (
              <FormControl variant="outlined" fullWidth margin="normal">
                <TextField
                  label="自定義標籤名稱"
                  placeholder="輸入標籤名稱（例如：討論、分析、總結）"
                  value={customTagName}
                  onChange={handleCustomTagChange}
                  variant="outlined"
                  helperText="請輸入簡潔的標籤名稱，建議使用中文或英文"
                />
              </FormControl>
            )}
          </>
        )}

        {/* 回覆模式：顯示要回覆的節點內容 */}
        {isReply && parentNodeContent && (
          <Box sx={{ 
            mb: 3, 
            p: 2.5, 
            backgroundColor: '#f8fafc', 
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <span style={{ 
                fontSize: '0.875rem', 
                color: '#64748b', 
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>正在回覆：</span>
              {showTags && (
                <Chip 
                  label={getTagDisplayText(selectedTag)}
                  style={{ 
                    backgroundColor: getTagColor(selectedTag),
                    color: '#1e293b',
                    fontWeight: 500,
                    fontSize: '0.8125rem'
                  }}
                  size="small"
                />
              )}
            </Box>
            <Box sx={{ 
              fontSize: '0.9375rem', 
              color: '#334155',
              lineHeight: 1.6,
              maxHeight: '140px',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#cbd5e1',
                borderRadius: '3px'
              }
            }}>
              <div 
                className="node-content-container"
                dangerouslySetInnerHTML={{ 
                  __html: parseContentToHtml(parentNodeContent.content || parentNodeContent.title || '') 
                }} 
              />
            </Box>
          </Box>
        )}

        {/* 標籤預覽 - 只在非回覆模式且非 nodePlan 模式顯示 */}
        {!isReply && !isNodePlanMode && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label={
                isCustomTag 
                  ? `✏️ ${customTagName || '自定義標籤'}`
                  : getTagDisplayText(selectedTag)
              }
              style={{ 
                backgroundColor: isCustomTag 
                  ? '#E5E7EB' 
                  : getTagColor(selectedTag),
                color: '#1e293b',
                fontWeight: 500
              }}
              size="small"
            />
          </Box>
        )}

        {/* 標題輸入 */}
        {isNodePlanMode && <Box sx={{ mb: 1.5, fontSize: '0.9375rem', fontWeight: 500, color: '#475569' }}>回應標題：</Box>}
        <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
          <TextField
            required
            id="standard-required"
            autoFocus
            margin="dense"
            label={isNodePlanMode ? safeNodePlan.label : "想法標題"}
            type="text"
            name='title'
            value={title}
            fullWidth
            variant="outlined"
            onChange={titleContentChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: '#ffffff',
                transition: 'all 0.2s',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                  borderWidth: '1.5px'
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e1'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                  borderWidth: '2px'
                }
              },
              '& .MuiInputLabel-root': {
                color: '#64748b',
                '&.Mui-focused': {
                  color: '#3b82f6'
                }
              }
            }}
          />
          <FormHelperText sx={{ mt: 1, fontSize: '0.8125rem', color: '#64748b' }}>
            {isNodePlanMode ? safeNodePlan.helperMsg : "請為你的想法下一個清楚的標題，讓其他同學能快速了解你的重點"}
          </FormHelperText>
        </FormControl>

        {/* 內容編輯器 - 使用 ForumEditor */}
        <Box sx={{ 
          mt: 3,
          minHeight: '300px',
          height: '350px',
          maxHeight: '600px',
          resize: 'vertical',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#fafafa',
          padding: '12px',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '4px',
            right: '4px',
            width: '16px',
            height: '16px',
            background: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%)',
            cursor: 'ns-resize',
            pointerEvents: 'none',
            zIndex: 10,
            borderRadius: '0 0 8px 0'
          },
          // 覆蓋 CoreForumEditor 外層 flex div
          '& > div': {
            height: '100% !important',
            display: 'flex !important',
            flexDirection: 'row !important',
            minHeight: 0
          },
          '& .editor-container': {
            margin: '0 !important',
            height: '100% !important',
            display: 'flex !important',
            flexDirection: 'column !important',
            maxWidth: '100% !important',
            minHeight: 0,
            border: 'none !important'
          },
          '& .editor-inner': {
            flex: '1 1 auto !important',
            overflowY: 'auto !important',
            overflowX: 'hidden !important',
            minHeight: 0,
            maxHeight: '100%',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#cbd5e1',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#94a3b8'
              }
            }
          },
          '& .lexical-toolbar': {
            flexShrink: 0,
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            borderRadius: '8px 8px 0 0'
          },
          '& .editor-input': {
            minHeight: '100px !important',
            height: 'auto !important',
            fontSize: '0.9375rem',
            lineHeight: 1.6
          }
        }}>
          <CoreForumEditor 
            changeFunc={(content) => {
              setContent(content);
            }}
            nodePlan={isNodePlanMode ? safeNodePlan : {
              saffoldBody: [
                "💡這邊我想提供一個我的想法",
                "🧐我覺得這邊應該要",
                "❓我想知道",
                "🙅🏻這不能解釋",
                "📄能針對 舉例嗎",
                "📄能針對 提供參考資料嗎",
                "✍🏻我到目前為止的想法是"
              ],
              scaffoldWords: [
                "這邊我想提供一個我的想法", 
                "我覺得這邊應該要", 
                "我想知道", 
                "這不能解釋", 
                "能針對 舉例嗎",
                "能針對 提供參考資料嗎", 
                "我到目前為止的想法是"
              ]
            }}
            graphNodes={graphNodes}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
        <Button 
          onClick={handleClose}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '0.9375rem',
            padding: '8px 20px',
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f5f9'
            }
          }}
        >
          取消
        </Button>
        <LoadingButton 
          type='submit' 
          onClick={handleSubmit} 
          loading={loading} 
          loadingPosition="start" 
          variant="contained"
          disabled={!title.trim() || !content || (isCustomTag && !customTagName.trim())}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '0.9375rem',
            padding: '8px 24px',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }
          }}
        >
          送出
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

